import { useEffect, useState } from "react";
import { Plus, Trash2, KeyRound, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PageHeader, Loading } from "@/components/Common";
import { SimpleSelect } from "@/components/Filters";
import { useAuth } from "@/context/AuthContext";
import { useLoad } from "@/hooks/useData";
import { api, errMsg } from "@/lib/api";
import { dateLabel } from "@/lib/format";

const ROLES = [["staff", "Staff"], ["viewer", "Viewer"], ["admin", "Admin"]];
const SECTION_LABELS = [
  ["stock", "Stock & reports", "Dashboard, product master, ledger, ASINs, plans, settings"],
  ["entries", "Daily IN / OUT entries", "Record stock IN, OUT, transfers and ASIN sales"],
  ["orders", "Sales & purchase orders", "Create, dispatch, receive and print orders"],
  ["shipments", "Shipments", "Shipment list, status changes and follow-up notes"],
  ["shipment_uploads", "Shipment uploads", "Upload order / courier files and paste statuses"],
  ["returns", "Returns", "View = Returns-view. Edit = Returns-receive & inspect"],
  ["returns_money", "Returns – refunds & claims", "Process refunds, raise and update claims"],
  ["returns_settings", "Returns – settings & import", "Dropdown lists, ageing, stock posting, CSV import"],
];

const PermissionsDialog = ({ u, onClose, onSaved }) => {
  const [p, setP] = useState({});
  useEffect(() => { if (u) setP(u.perms); }, [u]);
  if (!u) return null;
  const levels = (k) => (["shipment_uploads", "returns_money", "returns_settings"].includes(k) ? [["none", "No access"], ["edit", k === "shipment_uploads" ? "Can upload" : "Allowed"]] : u.role === "viewer" ? [["none", "No access"], ["view", "View only"]] : [["none", "No access"], ["view", "View only"], ["edit", "View & edit"]]);
  const save = async () => {
    try { await api.patch(`/users/${u.id}`, { permissions: p }); toast.success("Access updated"); onSaved(); onClose(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="permissions-dialog">
        <DialogHeader><DialogTitle>Access for {u.name || u.email}</DialogTitle></DialogHeader>
        <div className="divide-y divide-border">
          {SECTION_LABELS.map(([k, label, hint]) => (
            <div key={k} className="flex items-center justify-between gap-3 py-2.5">
              <div className="min-w-0"><div className="text-sm font-medium">{label}</div><div className="text-[11px] text-muted-foreground">{hint}</div></div>
              <SimpleSelect value={p[k] === "view" && k === "shipment_uploads" ? "none" : p[k] || "none"} onChange={(v) => setP((x) => ({ ...x, [k]: v }))} testid={`perm-${k}`} className="h-8 w-36" options={levels(k)} />
            </div>
          ))}
        </div>
        {u.role === "viewer" && <p className="text-xs text-muted-foreground">Viewers are always read-only. Change the role to Staff to allow editing.</p>}
        <DialogFooter><Button onClick={save} data-testid="perm-save-btn">Save access</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const NewUserDialog = ({ open, onOpenChange, onSaved }) => {
  const [f, setF] = useState({ name: "", email: "", password: "", role: "staff" });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", f);
      toast.success("User created");
      setF({ name: "", email: "", password: "", role: "staff" });
      onSaved();
      onOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="new-user-dialog">
        <DialogHeader><DialogTitle>Create user</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div className="space-y-1.5"><Label>Name</Label><Input required value={f.name} onChange={set("name")} data-testid="user-name-input" /></div>
          <div className="space-y-1.5"><Label>Email</Label><Input type="email" required value={f.email} onChange={set("email")} data-testid="user-email-input" /></div>
          <div className="space-y-1.5"><Label>Password (min 6)</Label><Input type="password" required minLength={6} value={f.password} onChange={set("password")} data-testid="user-password-input" /></div>
          <div className="space-y-1.5"><Label>Role</Label>
            <SimpleSelect value={f.role} onChange={set("role")} testid="user-role-select" className="h-9 w-full" options={ROLES} /></div>
          <DialogFooter><Button type="submit" data-testid="user-save-btn">Create</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function UsersPage() {
  const { user: me } = useAuth();
  const { data, reload } = useLoad(() => api.get("/users").then((r) => r.data));
  const [open, setOpen] = useState(false);
  const [permUser, setPermUser] = useState(null);
  if (!data) return <Loading />;
  const del = async (u) => {
    if (!window.confirm(`Delete ${u.email}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("User deleted"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const resetPw = async (u) => {
    const pw = window.prompt(`New password for ${u.email} (min 6 characters)`);
    if (!pw) return;
    try { await api.patch(`/users/${u.id}`, { password: pw }); toast.success("Password updated"); } catch (e) { toast.error(errMsg(e)); }
  };
  const setRole = async (u, role) => {
    try { await api.patch(`/users/${u.id}`, { role }); toast.success("Role updated"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="max-w-4xl">
      <PageHeader title="Users & access" subtitle="Admins have full access. Use the shield button to choose what each staff member or viewer can see or edit.">
        <Button size="sm" onClick={() => setOpen(true)} data-testid="new-user-btn"><Plus className="h-4 w-4" /> New user</Button>
      </PageHeader>
      <div className="panel overflow-auto">
        <table className="data-table" data-testid="users-table">
          <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Access</th><th>Created</th><th></th></tr></thead>
          <tbody>
            {data.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.email}`}>
                <td className="font-medium">{u.name}</td><td>{u.email}</td>
                <td>{u.id === me.id ? <span className="text-xs uppercase tracking-wider text-primary">{u.role} (you)</span> :
                  <SimpleSelect value={u.role} onChange={(r) => setRole(u, r)} testid={`user-role-${u.email}`} className="h-8 w-28" options={ROLES} />}</td>
                <td className="whitespace-normal text-[11px] text-muted-foreground max-w-[260px]" data-testid={`user-access-${u.email}`}>
                  {u.role === "admin" ? "Full access" : SECTION_LABELS.filter(([k]) => u.perms[k] !== "none").map(([k, l]) => `${l} (${u.perms[k] === "edit" ? "edit" : "view"})`).join(", ") || "No access"}</td>
                <td className="text-xs text-muted-foreground">{dateLabel(u.created_at?.slice(0, 10))}</td>
                <td className="whitespace-nowrap">
                  {u.role !== "admin" && <button className="p-1.5 text-muted-foreground hover:text-primary" onClick={() => setPermUser(u)} title="Access" data-testid={`perm-btn-${u.email}`}><ShieldCheck className="h-4 w-4" /></button>}
                  <button className="p-1.5 text-muted-foreground hover:text-primary" onClick={() => resetPw(u)} title="Reset password" data-testid={`reset-pw-${u.email}`}><KeyRound className="h-4 w-4" /></button>
                  {u.id !== me.id && <button className="p-1.5 text-muted-foreground hover:text-red-400" onClick={() => del(u)} data-testid={`delete-user-${u.email}`}><Trash2 className="h-4 w-4" /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <NewUserDialog open={open} onOpenChange={setOpen} onSaved={reload} />
      <PermissionsDialog u={permUser} onClose={() => setPermUser(null)} onSaved={reload} />
    </div>
  );
}
